import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {PopupService} from "../../../../services/popup.service";
import {animate, style, transition, trigger} from "@angular/animations";
import {EventTypes} from "../../../../event-types";
import {updateReport, updateSearchState} from "../../../../state/search/search.actions";
import {SharedService} from "../../../../services/shared.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../state/app.state";
import {markEventComplete} from "../../../../state/search/search.actions";
import {updateIsLoading} from "../../../../state/search/search.actions";
import {Subscription, take} from "rxjs";
import {selectAreAllEventsComplete} from "../../../../state/search/search.selectors";
import * as sea from "node:sea";
import {Company} from 'src/app/interfaces/company.interface';
import { normalizeCompanyName } from 'src/app/utils/normalize-company.util';
import { formatLargeNumber } from '../../../../utils/number.utils';
import {AssistantPromptComponent} from "../../../../common/assistant-prompt/assistant-prompt.component";


@Component({
  selector: 'app-company-expanded',
  templateUrl: './company-expanded.component.html',
  styleUrls: ['./company-expanded.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)' }), // start with translateY -100%
        animate('1s ease-out', style({ transform: 'translateY(0)' })) // animate to translateY 0 over 1 second with ease-out timing
      ]),
    ]),
  ],
})
export class CompanyExpandedComponent implements OnInit, OnChanges {

  @Input() reportSearch: any;
  @Input() company: Company;
  @Input() report: any;

  @Output() dataEmitter = new EventEmitter<any>();
  @Output() icpScoreClicked = new EventEmitter<void>();
  @Output() editPromptToggled = new EventEmitter<any>();
  @Output() companyDetailsModalRequested = new EventEmitter<any>();
  @Output() editRevenueModalRequested = new EventEmitter<{short: string, long: string}>();

  @ViewChild('revenuePrompt') revenuePrompt: AssistantPromptComponent;
  // @Input() icpScore: number;
  icpScore: number = null;

  isEstimatedRevenueVisible = false;
  isEstimatedFundingVisible = false;

  isCompanyRevenueLoading = false;
  companyRevenue: string = 'N/A';

  isCompanyProfitLoading = false;
  companyProfit: string = 'N/A';

  isCompanyFundingLoading = false;
  companyFunding: string = 'N/A';

  isCompanyEmployeesLoading = false;
  companyEmployees: string = 'N/A';

  formatNumber = formatLargeNumber;

  // Boolean flag to track the visibility of the modal
  isIcpScoreVisible: boolean = false;


  companies: any[] = [];

  private subscription!: Subscription;

  @Output() companySelected = new EventEmitter<any>();

  constructor(
    private popupService: PopupService,
    private sharedService: SharedService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
  ) {
    //
  }

  ngOnInit(): void {

    const searchId = this.reportSearch?.id;

    if (searchId) {
      // Check and update events based on uniqueProfile
      this.checkAndUpdateEventsFromUniqueProfile(searchId);
    }

    // Start listening for events
    this.listenEvents();

    this.populateCompanies();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('[company-expanded.component.ts] ngOnChanges')

    if (changes['company'] && changes['company'].currentValue) {
      // Update the ICP score when the company input changes
      this.icpScore = changes['company'].currentValue.icp_score;
      // console.log('ICP Score updated:', this.icpScore);
    }

    // Rebuild the switchable-company list if the report (and therefore the
    // person's employment history) is set or replaced after init — e.g. async
    // load or enrichment.
    if (changes['reportSearch'] && !changes['reportSearch'].firstChange) {
      this.populateCompanies();
    }
  }

  ngOnDestroy(): void {
    // Clean up the subscription to avoid memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  populateCompanies(): void {
    // Build the "switch company" list from the person's Apollo employment
    // history. Apollo exposes each past role by company NAME / organization_id
    // only — there are no LinkedIn URLs here (and the legacy job_history is
    // effectively always empty), so the list is keyed and matched on name.
    const history = this.getEmploymentHistory();
    const uniqueCompaniesMap = new Map<string, any>();

    history.forEach((job: any) => {
      const name = job?.organization_name || job?.company || job?.company_name || null;

      // Only list positions the person CURRENTLY holds — a null end date means
      // they still work there. (Apollo uses `end_date`, legacy job_history
      // uses `ends_at`.) Skip any role that has ended.
      const endDate = job?.end_date || job?.ends_at || null;
      if (endDate) {
        return;
      }

      // No name → nothing we can scan or match against. Skip it.
      const key = normalizeCompanyName(name);
      if (!key || uniqueCompaniesMap.has(key)) {
        return;
      }

      uniqueCompaniesMap.set(key, {
        search_id: this.reportSearch?.id,
        name,
        organization_id: job?.organization_id || null,
        logo_url: job?.logo_url || null,
        title: job?.title || null,
        // Apollo employment_history uses `start_date`; legacy job_history `starts_at`.
        start_date: job?.start_date || job?.starts_at || null,
        current: !!job?.current,
        // Kept for the (rare) data source that still carries a URL; absence is
        // expected for Apollo and the modal/scan no longer requires it.
        parsed_linkedin_url: job?.company_linkedin_profile_url || null,
      });
    });

    this.companies = Array.from(uniqueCompaniesMap.values());
  }

  /**
   * Returns the person's employment history, preferring Apollo's
   * `enhanced_data.employment_history` and falling back to the legacy
   * `job_history` so older reports keep working.
   */
  private getEmploymentHistory(): any[] {
    const person = this.reportSearch?.search_person;
    const employment = person?.enhanced_data?.employment_history;
    if (Array.isArray(employment) && employment.length) {
      return employment;
    }
    return person?.job_history || [];
  }

  private checkAndUpdateEventsFromUniqueProfile(searchId: number): void {
    const uniqueProfile = this.reportSearch?.uniqueProfile || {};

    const profileFields = [
      {
        eventType: EventTypes.COMPANY_REVENUE,
        key: 'company_revenue',
        setter: (value: string) => {
          this.companyRevenue = value;
          this.isCompanyRevenueLoading = false;
        },
      },
      {
        eventType: EventTypes.COMPANY_PROFIT,
        key: 'company_profit',
        setter: (value: string) => {
          this.companyProfit = value;
          this.isCompanyProfitLoading = false;
        },
      },
      {
        eventType: EventTypes.COMPANY_FUNDING,
        key: 'company_funding',
        setter: (value: string) => {
          this.companyFunding = value;
          this.isCompanyFundingLoading = false;
        },
      },
      {
        eventType: EventTypes.COMPANY_EMPLOYEES,
        key: 'company_employees',
        setter: (value: string) => {
          this.companyEmployees = value;
          this.isCompanyEmployeesLoading = false;
        },
      },
    ];

    profileFields.forEach(({ eventType, key, setter }) => {
      const value = uniqueProfile[key];
      if (value) {
        console.log('Marking event complete:', eventType);
        this.store.dispatch(markEventComplete({ searchId, eventType }));
        setter(value); // Populate the corresponding property
      }
    });
  }


  private handleEvent(event: any): void {
    console.log('handleEvent event:', event)
    const searchId = event.data.report_id;
    const eventType = event.type;

    switch (eventType) {
      case EventTypes.COMPANY_REVENUE:
        if (event.data.company_revenue !== null) {
          this.companyRevenue = event.data.company_revenue;
          this.isCompanyRevenueLoading = false;
        }
        break;

      case EventTypes.COMPANY_PROFIT:
        if (event.data.company_profit !== null) {
          this.companyProfit = event.data.company_profit;
          this.isCompanyProfitLoading = false;
        }
        break;

      case EventTypes.COMPANY_FUNDING:
        if (event.data.company_funding !== null) {
          this.companyFunding = event.data.company_funding;
          this.isCompanyFundingLoading = false;
        }
        break;

      case EventTypes.COMPANY_EMPLOYEES:
        if (event.data.company_employee_count !== null) {
          this.companyEmployees = event.data.company_employee_count;
          this.isCompanyEmployeesLoading = false;
        }
        break;

      default:
        console.warn('Unhandled event type:', eventType);
    }

    this.store.dispatch(markEventComplete({ searchId, eventType }));

    this.store
      .select(selectAreAllEventsComplete(searchId))
      .pipe(take(1))
      .subscribe((allComplete) => {
        if (allComplete) {
          console.log(`All events complete for searchId: ${searchId}`);

          this.store.dispatch(updateSearchState({
            searchId: searchId,
            updates: { is_loading: true }
          }));

        }
      });

  }

  private listenEvents(): void {
    // Only subscribe to events that this component actually handles
    this.sharedService.eventEmitter.subscribe((event) => {
      // Only handle events that are relevant to company-expanded
      const relevantEvents = [
        EventTypes.COMPANY_REVENUE,
        EventTypes.COMPANY_PROFIT,
        EventTypes.COMPANY_FUNDING,
        EventTypes.COMPANY_EMPLOYEES
      ];

      if (relevantEvents.includes(event.type)) {
        this.handleEvent(event);
      }
    });
  }

  expandCompany(companyExpanded: boolean) {
    this.dataEmitter.emit(!companyExpanded);
  }


  private hasFailedOnce = new Set<string>();
  onImageError(event: Event, item: any) {
    const imgElement = event.target as HTMLImageElement;

    if (!this.hasFailedOnce.has(item.logo_url)) {
      this.hasFailedOnce.add(item.logo_url);
      imgElement.src = 'assets/images/human.svg';
    } else {
      imgElement.style.display = 'none';
    }
  }

  descriptionPopup() {
    // this.popupService.info('Description', this.reportSearch.search_person.enhanced_data?.organization?.short_description);
    this.popupService.info('Description', this.company?.short_description);
  }

  readMore(){
    // return this.reportSearch.search_person.enhanced_data?.organization?.short_description?.length > 200

    if (this.company?.short_description) {
      return this.company?.short_description.length > 200
    }
    return false
  }

  showEstimedRevenueMessage() {

    this.isEstimatedRevenueVisible = true;
  }

  hideEstimedRevenueMessage() {
    this.isEstimatedRevenueVisible = false;
  }
  showEstimedFundingMessage() {

    this.isEstimatedFundingVisible = true;
  }

  hideEstimedFundingMessage() {
    this.isEstimatedFundingVisible = false;
  }

  openIcpScore() {
    this.icpScoreClicked.emit();
  }

  handleEditPrompt(assistantPrompt: string, promptValue: InnerHTML) {
    this.editPromptToggled.emit({
      visible: true,              // Show the modal
      assistantPrompt: assistantPrompt,
      promptValue: promptValue
    });
  }

  onIcpScoreClicked() {
    this.icpScoreClicked.emit();
  }

  openCompanyDetails(): void {
    // Read the ACTIVE company's veridion data so the details modal follows a
    // company switch (instead of the report-level / original company's data).
    if (this.company?.veridion_data?.result && this.company.veridion_data.result.length > 0) {
      this.companyDetailsModalRequested.emit(this.company.veridion_data.result[0]);
    }
  }

  openEditRevenueModalWithData(): void {
    // Extract shortContent and longContent from the AssistantPromptComponent
    const shortContent = this.revenuePrompt?.shortContent || '';
    const longContent = this.revenuePrompt?.longContent || '';

    this.editRevenueModalRequested.emit({
      short: shortContent,
      long: longContent
    });
  }

}
